import { useCallback, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { toaster } from "components/common/Toast"
import { deleteCrewTimeOff, getSingleCrewTimeOff, updateCrewTimeOff } from "store/crew-profiles"
import { useBookingConflict } from "hooks/useBookingConflict"
import { ROW_MENU_ACTIONS } from "pagesV2/pro/Settings/Blackout/constants"

/** The single time-off response, whichever envelope it arrives in. */
const toTimeOffRecord = (payload) => payload?.data?.time_off || payload?.data || payload?.time_off || payload || null

/**
 * The edit form's initial values — the same fields the crew page's Scheduled Time Off table hands
 * the modal, so the form fills the same way from either place.
 */
const toFormValues = (record) => ({
  id: record.id,
  start_date: record.start_date,
  end_date: record.end_date,
  start_time: record.start_time,
  end_time: record.end_time,
  part_day: record.part_day,
  reason: record.reason,
  notes: record.notes
})

/**
 * Edit and Delete for a Crew Off card.
 *
 * Crew off is a crew member's time off, not a blackout record, so its id means nothing to the
 * blackout endpoints — `DELETE /guide/blackouts/:id` with it would 404, or remove an unrelated
 * blackout sharing the number. These go to the crew time-off endpoints instead, which need the
 * crew member's profile id as well, and reuse the crew page's own time-off modal and copy.
 */
export const useCrewOffActions = ({ onChanged }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { handleBookingConflictError, bookingConflictModalProps } = useBookingConflict()

  /** `{ card, values }` while the edit modal is open. */
  const [editing, setEditing] = useState(null)
  const [cardToDelete, setCardToDelete] = useState(null)
  const [isFetching, setIsFetching] = useState(false)

  const isSaving = useSelector((state) => Boolean(state.crewProfiles?.updateTimeOff?.loading))
  const isDeleting = useSelector((state) => Boolean(state.crewProfiles?.deleteTimeOff?.loading))

  /** The form needs the whole entry; the day's reason only describes the part covering this day. */
  const openEdit = useCallback(
    (card) => {
      if (isFetching) return
      setIsFetching(true)
      dispatch(getSingleCrewTimeOff(card.profileId, card.id))
        .then((payload) => {
          const record = toTimeOffRecord(payload)
          if (record) setEditing({ card, values: toFormValues(record) })
        })
        .catch(() => {})
        .finally(() => setIsFetching(false))
    },
    [dispatch, isFetching]
  )

  const closeEdit = useCallback(() => setEditing(null), [])

  const saveEdit = useCallback(
    (values) => {
      if (!editing) return undefined
      return dispatch(updateCrewTimeOff(editing.card.profileId, editing.card.id, values))
        .then(() => {
          toaster.success(t("crewMember.scheduledTimeOff.messages.updateSuccess"))
          setEditing(null)
          onChanged?.()
        })
        .catch(handleBookingConflictError)
    },
    [dispatch, editing, handleBookingConflictError, onChanged, t]
  )

  const confirmDelete = useCallback(() => {
    if (!cardToDelete) return
    dispatch(deleteCrewTimeOff(cardToDelete.profileId, cardToDelete.id))
      .then(() => {
        toaster.success(t("crewMember.scheduledTimeOff.messages.deleteSuccess"))
        onChanged?.()
      })
      .catch(() => {})
      .finally(() => setCardToDelete(null))
  }, [dispatch, cardToDelete, onChanged, t])

  const cancelDelete = useCallback(() => setCardToDelete(null), [])

  const getMenuItems = useCallback(
    (card) => [
      {
        key: ROW_MENU_ACTIONS.EDIT,
        label: t("settings.action_edit", { defaultValue: "Edit" }),
        disabled: isFetching,
        onClick: () => openEdit(card)
      },
      {
        key: ROW_MENU_ACTIONS.DELETE,
        label: t("settings.action_delete", { defaultValue: "Delete" }),
        danger: true,
        onClick: () => setCardToDelete(card)
      }
    ],
    [isFetching, openEdit, t]
  )

  return {
    getMenuItems,
    editing,
    closeEdit,
    saveEdit,
    isSaving,
    cardToDelete,
    confirmDelete,
    cancelDelete,
    isDeleting,
    bookingConflictModalProps
  }
}

export default useCrewOffActions
